import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
} from "@coreui/react";
import { useCallback, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  createItem,
  updateItem,
  ItemRequestStatus,
  getList,
} from "../../api/generics";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert } from "../utils/messages";
import { emptyValueOnUndefined } from "../../utils/fields";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import EmailGroup, {
  EmailGroupItem,
  newEmailGroup,
} from "../../models/email-groups";
import {
  listToUniqueListItems,
  newUniqueListItem,
  UniqueListItem,
  uniqueListToListItems,
} from "../../models/unique-list-item";
import AnimatedCheckbox from "../checkbox/AnimatedCheckbox";
import EmailGroupItemForm from "./EmailGroupItemForm";
import DocumentCategoriesSelect from "../document-category/DocumentCategoriesSelect";
import DocumentCategory from "../../models/document-category";

interface EmailGroupFormProps {
  initialEmailGroup?: EmailGroup;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const EmailGroupForm: React.FC<EmailGroupFormProps> = ({
  initialEmailGroup,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const company = useSelector((state: RootState) => state.company.data.company);
  const [editingEmailGroup, setEditingEmailGroup] = useState<EmailGroup>(
    initialEmailGroup ? initialEmailGroup : newEmailGroup()
  );
  const [categories, setCategories] = useState<readonly DocumentCategory[]>([]);
  const [emailGroupItems, setEmailGroupItems] = useState<
    UniqueListItem<EmailGroupItem>[]
  >([]);
  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingEmailGroup({
      ...editingEmailGroup,
      name: e.target.value,
    });
  };

  const onActiveChange = (isActive: boolean) => {
    setEditingEmailGroup({
      ...editingEmailGroup,
      active: isActive,
    });
  };

  const onAddEntryClick = () => {
    setEmailGroupItems((items) => {
      const newEntries = [
        ...items,
        newUniqueListItem<EmailGroupItem>({ type: "email" }),
      ];
      return newEntries;
    });
  };

  const onItemEntryChange = (entry: UniqueListItem<EmailGroupItem>) => {
    setEmailGroupItems((entries) => {
      const newEntries = [...entries];
      const index = newEntries.findIndex((item) => {
        return item.uuid === entry.uuid;
      });

      if (index === -1) {
        return newEntries;
      }

      newEntries[index] = entry;

      return newEntries;
    });
  };

  const onItemEntryDelete = (entry: UniqueListItem<EmailGroupItem>) => {
    setEmailGroupItems((entries) => {
      const newEntries = [...entries];

      const index = newEntries.findIndex((item) => {
        return item.uuid === entry.uuid;
      });

      if (index === -1) {
        return newEntries;
      }

      newEntries.splice(index, 1);

      return newEntries;
    });
  };

  const onSave = async () => {
    setSubmitting(true);
    const mutableCategories: DocumentCategory[] = Array.from(categories);
    /*if (mutableCategories.length === 0) {
      setErrors({
        categories: ["Debe seleccionar por lo menos una categoría"],
      });
      setSubmitting(false);
      return;
    }*/
    // Add companyId to each item before sending
    const toSendItems = uniqueListToListItems(emailGroupItems).map((item) => ({
      ...item,
      companyId: company.id,
      user: item.user
        ? { ...item.user, permissions: item.user.permissions ?? [] }
        : undefined,
    }));
    if (toSendItems.length === 0) {
      errorAlert("Debe agregar al menos un destinatario al grupo de correos.");
      setSubmitting(false);
      return;
    }
    let toSendEmailGroup = {
      ...editingEmailGroup,
      companyId: company.id,
      items: toSendItems,
      categories: mutableCategories,
    };
    let requestPromise: Promise<ItemRequestStatus<EmailGroup>>;
    if (editingEmailGroup.id === undefined) {
      requestPromise = createItem<EmailGroup>(
        "/email_groups/create/",
        toSendEmailGroup
      );
    } else {
      requestPromise = updateItem<EmailGroup>(
        `/email_groups/${toSendEmailGroup.id}/`,
        toSendEmailGroup
      );
    }

    const emailGroupStatus = await requestPromise;

    if (emailGroupStatus.status !== SUCCESS) {
      if (emailGroupStatus.errors !== undefined) {
        setErrors(emailGroupStatus.errors);
      }

      let message = "Ha ocurrido un error!!";
      if (emailGroupStatus.detail !== undefined) {
        message = emailGroupStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      onSuccess();
    }
    setSubmitting(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const fetchCategories = useCallback(async () => {
    if (editingEmailGroup === undefined || editingEmailGroup.id === undefined) {
      setCategories([]);
      return [];
    }
    const groupIdStr = editingEmailGroup.id.toString();
    if (groupIdStr === undefined) {
      setCategories([]);
      return [];
    }
    const limit = 100;
    const offset = 0;
    const additional = new Map();
    additional.set("group_id", groupIdStr);
    additional.set("company_id", company.id!.toString());
    const categoriesStatus = await getList<DocumentCategory>(
      "/document_categories/",
      limit,
      offset,
      additional
    );

    if (categoriesStatus.status === SUCCESS) {
      if (categoriesStatus.data !== undefined) {
        setCategories(categoriesStatus.data.items);
      }
    } else {
      setCategories([]);
    }
  }, [editingEmailGroup]);

  const fetchItems = useCallback(async () => {
    if (initialEmailGroup === undefined || initialEmailGroup.id === undefined) {
      setEmailGroupItems([]);
      return [];
    }
    const saleOrderIdStr = initialEmailGroup.id.toString();
    if (saleOrderIdStr === undefined) {
      setEmailGroupItems([]);
      return [];
    }
    const limit = 5000;
    const offset = 0;
    let additionalParams: Map<string, string> | undefined = new Map<
      string,
      string
    >();

    additionalParams.set("company_id", company.id!.toString());
    additionalParams.set("email_group_id", saleOrderIdStr);
    const itemsStatus = await getList<EmailGroupItem>(
      `/email_groups/items/`,
      limit,
      offset,
      additionalParams
    );
    if (itemsStatus.status === SUCCESS) {
      if (itemsStatus.data !== undefined) {
        const items = listToUniqueListItems(itemsStatus.data.items);
        setEmailGroupItems(items);
      }
    } else {
      setEmailGroupItems([]);
    }
  }, [editingEmailGroup]);

  const clearForm = () => {
    setEmailGroupItems([]);
    setEditingEmailGroup(newEmailGroup());
  };

  const getInitialValues = useCallback(async () => {
    setLoading(true);
    const categoriesPromise = fetchCategories();
    const itemsPromise = fetchItems();
    await categoriesPromise;
    await itemsPromise;
    setLoading(false);
  }, [editingEmailGroup]);

  useEffect(() => {
    setEditingEmailGroup(
      initialEmailGroup ? initialEmailGroup : newEmailGroup()
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEmailGroup]);

  useEffect(() => {
    getInitialValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingEmailGroup]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);

  const itemsErrors = getFieldErrors("items", errors) as Errors[];

  return (
    <>
      <fieldset disabled={submitting}>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Nombre:</CLabel>
            </CCol>
            <CCol>
              <CInput
                type="text"
                value={emptyValueOnUndefined(editingEmailGroup.name)}
                onChange={onNameChange}
                placeholder="Nombre"
              ></CInput>
              <FieldErrors
                errors={getFieldErrors("name", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Activo:</CLabel>
            </CCol>
            <CCol md={2}>
              <CRow>
                <AnimatedCheckbox
                  value={
                    editingEmailGroup.active !== undefined
                      ? editingEmailGroup.active
                      : false
                  }
                  onChange={onActiveChange}
                ></AnimatedCheckbox>
              </CRow>
            </CCol>
          </CRow>
        </CFormGroup>
        <CFormGroup>
          <CRow>
            <CCol md={2}>
              <CLabel>Categorías:</CLabel>
            </CCol>
            <CCol>
              <DocumentCategoriesSelect
                value={categories}
                onChange={setCategories}
                isDisabled={loading || submitting}
              ></DocumentCategoriesSelect>
              <FieldErrors
                errors={getFieldErrors("categories", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
        {emailGroupItems.map((entry, ix) => {
          return (
            <CRow key={entry.uuid}>
              <CCol>
                <EmailGroupItemForm
                  value={entry.item}
                  onDelete={() => onItemEntryDelete(entry)}
                  disabled={false}
                  onChange={(item) =>
                    onItemEntryChange({
                      uuid: entry.uuid,
                      item: item,
                    })
                  }
                  errors={ix < itemsErrors.length ? itemsErrors[ix] : {}}
                />
              </CCol>
            </CRow>
          );
        })}
        {emailGroupItems.length === 0 ? (
          <CRow>
            <CCol>Agregue nuevas entradas con el boton + de la derecha</CCol>
          </CRow>
        ) : (
          <></>
        )}
        <CRow className="mb-1">
          <CCol>
            <CButton
              className="btn btn-primary float-right"
              onClick={onAddEntryClick}
              disabled={false}
            >
              <i className="fa fa-plus" />
            </CButton>
          </CCol>
        </CRow>
        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {submitting ? "Guardando..." : "Guardar"}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default EmailGroupForm;
